/**
 * PhotoCapturePanel (ported from Noteble — ADR-022).
 *
 * Shows a live camera stream with a capture button; captured photos are
 * added to AddContext (which uploads them to the tenant media store and
 * runs vision analysis).
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { AlertCircle, Image } from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { useAdd } from './AddContext';
import { createLogger } from '@/lib/logger';
import { ListItemCard } from './ListItemCard';

const log = createLogger('PhotoCapturePanel');

/** Capture a frame from the video stream and compress to JPEG. */
function capturePhotoFromStream(video: HTMLVideoElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const maxWidth = 1920;
    const aspectRatio = video.videoHeight / video.videoWidth;

    canvas.width = Math.min(video.videoWidth, maxWidth);
    canvas.height = canvas.width * aspectRatio;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob from canvas'));
      },
      'image/jpeg',
      0.85,
    );
  });
}

interface PhotoCapturePanelProps {
  onDiscard?: () => void;
}

export const PhotoCapturePanel: React.FC<PhotoCapturePanelProps> = ({ onDiscard }) => {
  const { addPhoto } = useAdd();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isStreamActive, setIsStreamActive] = useState(false);
  const [error, setError] = useState<string>('');
  const [isCapturing, setIsCapturing] = useState(false);

  const startCamera = useCallback(async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Rear camera on mobile
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          aspectRatio: { ideal: 16 / 9 },
        },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsStreamActive(true);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      log.error('Failed to start camera', err as Error);

      if (errorMessage.includes('Permission denied') || errorMessage.includes('NotAllowedError')) {
        setError('Camera access denied. Please allow camera access and try again.');
      } else {
        setError('Could not start the camera.');
      }
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setIsStreamActive(false);
    }
  }, []);

  const handleCapturePhoto = useCallback(async () => {
    if (!videoRef.current || !isStreamActive) return;

    try {
      setIsCapturing(true);
      const blob = await capturePhotoFromStream(videoRef.current);
      addPhoto(blob);
    } catch (err) {
      log.error('Failed to capture photo', err as Error);
      setError('Could not capture the photo.');
    } finally {
      setIsCapturing(false);
    }
  }, [isStreamActive, addPhoto]);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [startCamera, stopCamera]);

  return (
    <ListItemCard
      icon={
        <div className="p-2 bg-emerald-100 dark:bg-emerald-900/40 rounded-xl">
          <Image className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        </div>
      }
      onSend={handleCapturePhoto}
      sendDisabled={!isStreamActive || isCapturing}
      sendLabel="Capture"
      onDiscard={onDiscard || (() => {})}
      discardLabel="Discard"
    >
      {/* Camera preview — landscape 16:9, capped height */}
      <div
        className="relative bg-slate-900 rounded-xl overflow-hidden w-full max-h-[30vh]"
        style={{ aspectRatio: '16/9' }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />

        {!isStreamActive && !error ? (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
            <div className="text-center">
              <Spinner size="lg" className="mx-auto mb-3" />
              <p className="text-white text-sm">Starting camera…</p>
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/90">
            <div className="text-center px-6">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-white mb-4">{error}</p>
              <button
                type="button"
                onClick={startCamera}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </ListItemCard>
  );
};
